/**
 * DocumentationHubPage Component
 * AIDLC Administrative Backend
 *
 * Landing page for the Documentation section. Displays cards linking
 * to all available documentation: Deployment Guide, Email Management,
 * the AIDLC User Guide and the Executive Briefs library.
 *
 * Each card provides a brief summary and links to the full doc page.
 * Cards are organized by category: Getting Started, Briefings, Features, Operations.
 */

import { useNavigate } from 'react-router-dom';
import { Card } from '../shared/Card';
import { Badge } from '../shared/Badge';

// ─── Types ────────────────────────────────────────────────────────

interface DocCard {
  title: string;
  description: string;
  path: string;
  icon: React.ReactNode;
  badge: { label: string; variant: 'info' | 'success' | 'warning' | 'purple' | 'neutral' };
  highlights: string[];
}

// ─── Data ─────────────────────────────────────────────────────────

const DOCS: { category: string; items: DocCard[] }[] = [
  {
    category: 'Getting Started',
    items: [
      {
        title: 'AIDLC User Guide',
        description: 'Complete guide to the AIDLC Admin Portal — navigation, personas, dashboard, agents, chat, and day-to-day operations.',
        path: '/docs/user-guide',
        icon: (
          <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
          </svg>
        ),
        badge: { label: 'Start Here', variant: 'success' },
        highlights: [
          'Persona-based navigation',
          'Agent dashboard overview',
          'Chat interface usage',
          'Settings and preferences',
        ],
      },
    ],
  },
  {
    category: 'Briefings',
    items: [
      {
        title: 'Executive Briefs',
        description: 'Short, self-contained reports agents write for the CEO on one topic, such as the AI Governance Program brief. Each update keeps the same brief and raises its version.',
        path: '/docs/briefs',
        icon: (
          <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
          </svg>
        ),
        badge: { label: 'New', variant: 'info' },
        highlights: [
          'Written by the agent team',
          'Every update versioned',
          'Shown safely, with no scripts',
          'Linked to project and backlog item',
        ],
      },
    ],
  },
  {
    category: 'Features',
    items: [
      {
        title: 'Email Management',
        description: 'Inbox integration, email signature configuration, agent-assisted reply drafting, and OneDrive file browser.',
        path: '/docs/email',
        icon: (
          <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
          </svg>
        ),
        badge: { label: 'New', variant: 'info' },
        highlights: [
          'Inbox read & reply',
          'LDAJA branded signature',
          'Agent-drafted replies',
          'OneDrive file browser',
        ],
      },
    ],
  },
  {
    category: 'Operations',
    items: [
      {
        title: 'Deployment Guide',
        description: 'Step-by-step guide for deploying the AIDLC Admin Portal to Azure App Service, with interactive checklists and milestone gates.',
        path: '/deployment-guide',
        icon: (
          <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-6.25 0" />
          </svg>
        ),
        badge: { label: 'Azure', variant: 'purple' },
        highlights: [
          'Interactive pre-flight checklist',
          '5 milestone verification gates',
          'Rollback procedures',
          'Common mistakes & safeguards',
        ],
      },
    ],
  },
];

// ─── Component ────────────────────────────────────────────────────

export function DocumentationHubPage() {
  const navigate = useNavigate();

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Page Header */}
      <div>
        <h1 className="text-xl font-bold text-ldaja-black">Documentation</h1>
        <p className="text-sm text-gray-500 mt-1">
          Guides, references, and how-tos for the AIDLC Admin Portal
        </p>
        <div className="flex items-center gap-3 mt-2 flex-wrap">
          <Badge variant="info">AIDLC v2.0.0</Badge>
          <Badge variant="neutral">Last updated Feb 2026</Badge>
        </div>
      </div>

      {/* Quick Links */}
      <Card title="Quick Links" subtitle="Jump to a specific topic">
        <div className="flex flex-wrap gap-2">
          {DOCS.flatMap(cat => cat.items).map((doc) => (
            <button
              key={doc.path}
              onClick={() => navigate(doc.path)}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-gray-50 hover:bg-gray-100 border border-gray-200 transition-colors text-xs font-medium text-gray-700"
            >
              <span className="text-ldaja-blue w-4 h-4">{doc.icon}</span>
              {doc.title}
            </button>
          ))}
        </div>
      </Card>

      {/* Doc Categories */}
      {DOCS.map((category) => (
        <div key={category.category}>
          <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
            {category.category}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {category.items.map((doc) => (
              <button
                key={doc.path}
                onClick={() => navigate(doc.path)}
                className="text-left group bg-white border border-gray-200 rounded-lg p-5 hover:border-ldaja-blue/30 hover:shadow-sm transition-all"
              >
                <div className="flex items-start gap-4">
                  <div className="text-ldaja-blue/70 group-hover:text-ldaja-blue transition-colors flex-shrink-0 mt-0.5">
                    {doc.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-sm font-semibold text-ldaja-black group-hover:text-ldaja-blue transition-colors">
                        {doc.title}
                      </h3>
                      <Badge variant={doc.badge.variant}>{doc.badge.label}</Badge>
                    </div>
                    <p className="text-xs text-gray-500 leading-relaxed mb-3">
                      {doc.description}
                    </p>
                    <ul className="space-y-1">
                      {doc.highlights.map((h) => (
                        <li key={h} className="flex items-center gap-2 text-xs text-gray-400">
                          <svg className="w-3 h-3 text-green-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                            <path d="M4.5 12.75l6 6 9-13.5" />
                          </svg>
                          {h}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div className="flex items-center justify-end mt-3">
                  <span className="text-xs text-ldaja-blue/60 group-hover:text-ldaja-blue transition-colors font-medium">
                    View documentation →
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      ))}

      {/* Footer */}
      <div className="text-center py-4 border-t border-gray-100">
        <p className="text-[10px] text-gray-400">
          Documentation is maintained by the AIDLC agent team. For updates, contact Dan (Ops) or use the Chat Interface.
        </p>
      </div>
    </div>
  );
}
